import { Injectable, computed, signal } from '@angular/core';
import { Document } from '../../models/document';
import {
  DocumentFormSnapshot,
  DocumentFilingState,
  EMPTY_FILING_STATE
} from '../../models/documentFiling.model';
import { KeyFieldsFormData } from '../document-management/document-management.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentFilingService {
  private readonly _state = signal<DocumentFilingState>({ ...EMPTY_FILING_STATE });
  private readonly _formSnapshots = new Map<string, DocumentFormSnapshot>();
  private readonly _manuallyChangedDocIds = new Set<string>();
  private readonly _areaOverrides = new Map<string, string>();
  private readonly _documentTypeOverrides = new Map<string, string>();
  private readonly _batchDefaultExtractionDocIds = new Set<string>();

  readonly session = this._state.asReadonly();

  readonly selectedDocument = computed<Document | null>(() => {
    const { documents, selectedDocumentId } = this._state();
    if (!selectedDocumentId) return null;
    return documents.find(d => d.fileId === selectedDocumentId) ?? null;
  });

  readonly selectedIndex = computed<number>(() => {
    const { documents, selectedDocumentId } = this._state();
    if (!selectedDocumentId) return -1;
    return documents.findIndex(d => d.fileId === selectedDocumentId);
  });

  readonly documents = computed<Document[]>(() => this._state().documents);
  readonly isLoading = computed<boolean>(() => this._state().isLoading);

  /**
   * Replaces the document list and resets selection.
   * Call this once after all documents have been loaded for a new filing workflow.
   * @param documents
   */
  setDocuments(documents: Document[]): void {
    this._state.update(s => ({ ...s, documents: [...documents] }));
  }

  /**
   * Marks the filing workflow as loading or not loading.
   * @param isLoading
   */
  setLoading(isLoading: boolean): void {
    this._state.update(s => ({ ...s, isLoading }));
  }

  /**
   * Resets all state back to the initial empty state.
   * Call this when the user restarts filing or navigates away.
   */
  reset(): void {
    this._state.set({ ...EMPTY_FILING_STATE });
    this._formSnapshots.clear();
    this._manuallyChangedDocIds.clear();
    this._areaOverrides.clear();
    this._documentTypeOverrides.clear();
    this._batchDefaultExtractionDocIds.clear();
  }

  /**
   * Sets the currently selected document by fileId.
   * Callers are responsible for flushing pending saves before calling this
   * (see KeyFieldsComponent.flushPendingChanges).
   * @param fileId
   */
  selectDocument(fileId: string): void {
    const exists = this._state().documents.some(d => d.fileId === fileId);
    if (!exists) {
      console.warn(`[DocumentFilingService] selectDocument: fileId "${fileId}" not found.`);
      return;
    }
    this._state.update(s => ({ ...s, selectedDocumentId: fileId }));
  }

  /**
   * Clears the current selection (e.g. after all documents are filed).
   */
  clearSelection(): void {
    this._state.update(s => ({ ...s, selectedDocumentId: null }));
  }

  /**
   * Appends a single document to the filing list.
   * @param document
   */
  addDocument(document: Document): void {
    this._state.update(s => ({ ...s, documents: [...s.documents, document] }));
  }

  /**
   * Appends multiple documents to the filing list.
   * @param documents
   */
  addDocuments(documents: Document[]): void {
    this._state.update(s => ({ ...s, documents: [...s.documents, ...documents] }));
  }

  /**
   * Applies a partial update to a specific document, identified by fileId.
   * All other documents and state are left unchanged.
   * @param fileId
   * @param partial
   */
  patchDocument(fileId: string, partial: Partial<Document>): void {
    this._state.update(s => ({
      ...s,
      documents: s.documents.map(d => (d.fileId === fileId ? { ...d, ...partial } : d))
    }));
  }

  /**
   * Removes a document from the filing list by fileId.
   * If the removed document was selected, selection moves to the next
   * available unfinished document, or clears if none remain.
   * @param fileId
   */
  removeDocument(fileId: string): void {
    const current = this._state();
    const remaining = current.documents.filter(d => d.fileId !== fileId);

    let nextSelectedId = current.selectedDocumentId;

    if (current.selectedDocumentId === fileId) {
      const nextDoc = remaining.find(d => !d.completed && !d.canceled);
      nextSelectedId = nextDoc?.fileId ?? remaining[0]?.fileId ?? null;
    }

    this._state.update(s => ({
      ...s,
      documents: remaining,
      selectedDocumentId: nextSelectedId
    }));

    this._formSnapshots.delete(fileId);
    this._manuallyChangedDocIds.delete(fileId);
    this._areaOverrides.delete(fileId);
    this._documentTypeOverrides.delete(fileId);
    this._batchDefaultExtractionDocIds.delete(fileId);
  }

  /**
   * Stores a form snapshot for a document.
   * Call this synchronously on every form value change, capturing the fileId
   * and form values at the moment of the change — not at save time.
   * @param fileId
   * @param formValue
   */
  updateFormSnapshot(fileId: string, formValue: KeyFieldsFormData): void {
    this._formSnapshots.set(fileId, { documentId: fileId, formValue, isDirty: true });
  }

  /**
   * Returns the latest form snapshot for a document, or null if none exists.
   * @param fileId
   */
  getFormSnapshot(fileId: string): DocumentFormSnapshot | null {
    return this._formSnapshots.get(fileId) ?? null;
  }

  /**
   * Marks a document's snapshot as clean (persisted to the backend).
   * @param fileId
   */
  markSnapshotClean(fileId: string): void {
    const snapshot = this._formSnapshots.get(fileId);
    if (snapshot) {
      this._formSnapshots.set(fileId, { ...snapshot, isDirty: false });
    }
  }

  /**
   * Removes the form snapshot for a document.
   * Call this when a document is deleted or the workflow resets.
   * @param fileId
   */
  clearFormSnapshot(fileId: string): void {
    this._formSnapshots.delete(fileId);
  }

  /**
   * Returns true if the document has a dirty (unsaved) snapshot.
   * @param fileId
   */
  hasUnsavedChanges(fileId: string): boolean {
    return this._formSnapshots.get(fileId)?.isDirty ?? false;
  }

  /**
   * Marks a document as having been manually edited by the user.
   * Once marked, autoclassification results will not overwrite the form.
   * @param fileId
   */
  markAsManuallyChanged(fileId: string): void {
    this._manuallyChangedDocIds.add(fileId);
  }

  /**
   * Returns true if the user has manually edited this document.
   * @param fileId
   */
  wasManuallyChanged(fileId: string): boolean {
    return this._manuallyChangedDocIds.has(fileId);
  }

  /**
   * Clears the manually-changed flag for a document.
   * Call this when re-extraction is explicitly triggered by the user.
   * @param fileId
   */
  clearManuallyChanged(fileId: string): void {
    this._manuallyChangedDocIds.delete(fileId);
  }

  /**
   * Stores a user-selected area override for a document.
   * When set, buildFormFromExtraction will use this area instead of
   * the area returned by the doc type API lookup.
   * @param fileId - The document to set the override for
   * @param areaCode - The user-selected area code
   */
  setAreaOverride(fileId: string, areaCode: string): void {
    this._areaOverrides.set(fileId, areaCode);
  }

  /**
   * Returns the user-selected area override for a document, or null if none.
   * @param fileId - The document to check
   */
  getAreaOverride(fileId: string): string | null {
    return this._areaOverrides.get(fileId) ?? null;
  }

  /**
   * Clears the area override for a document.
   * @param fileId - The document to clear the override for
   */
  clearAreaOverride(fileId: string): void {
    this._areaOverrides.delete(fileId);
  }

  /**
   * Stores a user-selected document type override for a document.
   * When set, buildFormFromExtraction will use this document type (with empty
   * key fields) instead of an empty form if extraction hasn't produced usable data yet.
   * @param fileId - The document to set the override for
   * @param documentTypeCode - The user-selected document type code
   */
  setDocumentTypeOverride(fileId: string, documentTypeCode: string): void {
    this._documentTypeOverrides.set(fileId, documentTypeCode);
  }

  /**
   * Returns the user-selected document type override for a document, or null if none.
   * @param fileId - The document to check
   */
  getDocumentTypeOverride(fileId: string): string | null {
    return this._documentTypeOverrides.get(fileId) ?? null;
  }

  /**
   * Clears the document type override for a document.
   * @param fileId - The document to clear the override for
   */
  clearDocumentTypeOverride(fileId: string): void {
    this._documentTypeOverrides.delete(fileId);
  }

  /**
   * Records that extraction has been re-run against the batch default
   * document type for a document whose auto-classification did not succeed.
   * Used to ensure the default-driven extraction runs at most once per document.
   * @param fileId - The document the default extraction was triggered for
   */
  markBatchDefaultExtractionTriggered(fileId: string): void {
    this._batchDefaultExtractionDocIds.add(fileId);
  }

  /**
   * Returns true if extraction has already been re-run against the batch
   * default document type for a document.
   * @param fileId - The document to check
   */
  wasBatchDefaultExtractionTriggered(fileId: string): boolean {
    return this._batchDefaultExtractionDocIds.has(fileId);
  }

  /**
   * Returns the first document that is not completed and not canceled,
   * or the first document overall if all are complete.
   */
  getFirstSelectableDocument(): Document | null {
    const docs = this._state().documents;
    if (docs.length === 0) return null;
    return docs.find(d => !d.completed && !d.canceled) ?? docs[0];
  }

  /**
   * Returns a document by fileId, or null if not found.
   * @param fileId
   */
  getDocument(fileId: string): Document | null {
    return this._state().documents.find(d => d.fileId === fileId) ?? null;
  }
}
